import { EnhancedSearchConsoleData } from '../types';

export interface TopicCluster {
  id: string;
  name: string;
  keywords: EnhancedSearchConsoleData[];
  totalClicks: number;
  totalImpressions: number;
  averagePosition: number;
  averageCTR: number;
  topPages: string[];
  healthScore: number; // 0-100 score based on performance metrics
  healthGrade: 'A' | 'B' | 'C' | 'D' | 'F'; // Letter grade for health
  recommendations: string[]; // Actionable recommendations
  priority: 'high' | 'medium' | 'low'; // Priority based on opportunity
  difficulty: 'easy' | 'medium' | 'hard'; // Based on average position
  opportunity: 'high' | 'medium' | 'low'; // Based on clicks potential
  trendIndicator: 'improving' | 'stable' | 'declining'; // Performance trend
  forecastMessage: string; // Predicted outcome based on current trajectory
  potentialClicks: number; // Estimated clicks if optimized
}

export interface TopicClustersResult {
  clusters: TopicCluster[];
  method: 'word-based' | 'semantic-similarity' | 'page-based';
  totalClusters: number;
  totalKeywordsInClusters: number;
}

// Helper function to calculate cluster metrics
const calculateClusterMetrics = (keywords: EnhancedSearchConsoleData[]): {
  totalClicks: number;
  totalImpressions: number;
  averagePosition: number;
  averageCTR: number;
  topPages: string[];
  healthScore: number;
  healthGrade: 'A' | 'B' | 'C' | 'D' | 'F';
  recommendations: string[];
  priority: 'high' | 'medium' | 'low';
  difficulty: 'easy' | 'medium' | 'hard';
  opportunity: 'high' | 'medium' | 'low';
  trendIndicator: 'improving' | 'stable' | 'declining';
  forecastMessage: string;
  potentialClicks: number;
} => {
  const totalClicks = keywords.reduce((sum, k) => sum + k.clicks, 0);
  const totalImpressions = keywords.reduce((sum, k) => sum + k.impressions, 0);
  const averagePosition = keywords.reduce((sum, k, _, arr) => sum + k.position / arr.length, 0);
  const averageCTR = totalImpressions > 0 ? totalClicks / totalImpressions : 0;
  
  // Get top 3 pages by clicks
  const pageClicks = new Map<string, number>();
  keywords.forEach(k => {
    if (k.page) {
      pageClicks.set(k.page, (pageClicks.get(k.page) || 0) + k.clicks);
    }
  });
  
  const topPages = Array.from(pageClicks.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(entry => entry[0]);

  // Calculate Health Score (0-100) based on multiple factors
  let healthScore = 0;
  
  // Position score (40 points): Better position = higher score
  const positionScore = Math.max(0, Math.min(40, 40 - (averagePosition * 2)));
  healthScore += positionScore;
  
  // CTR score (30 points): Higher CTR = higher score
  const expectedCTR = averagePosition <= 3 ? 0.15 : averagePosition <= 10 ? 0.05 : 0.02;
  const ctrScore = Math.min(30, (averageCTR / expectedCTR) * 30);
  healthScore += ctrScore;
  
  // Click performance (20 points): More clicks = higher score
  const avgClicksPerKeyword = totalClicks / keywords.length;
  const clickScore = Math.min(20, avgClicksPerKeyword * 2);
  healthScore += clickScore;
  
  // Impression utilization (10 points): High impressions with good CTR
  const impressionScore = totalImpressions > 1000 ? 10 : (totalImpressions / 100);
  healthScore += Math.min(10, impressionScore);
  
  healthScore = Math.round(Math.min(100, Math.max(0, healthScore)));
  
  // Determine health grade
  let healthGrade: 'A' | 'B' | 'C' | 'D' | 'F';
  if (healthScore >= 80) healthGrade = 'A';
  else if (healthScore >= 65) healthGrade = 'B';
  else if (healthScore >= 50) healthGrade = 'C';
  else if (healthScore >= 35) healthGrade = 'D';
  else healthGrade = 'F';
  
  // Generate actionable recommendations
  const recommendations: string[] = [];
  
  if (averagePosition > 10) {
    recommendations.push('📈 Improve content quality and on-page SEO to boost rankings from position ' + averagePosition.toFixed(1));
  }
  
  if (averageCTR < expectedCTR * 0.7) {
    recommendations.push('✏️ Optimize meta titles and descriptions to improve CTR (currently ' + (averageCTR * 100).toFixed(1) + '%)');
  }
  
  if (totalImpressions > 1000 && totalClicks < 50) {
    recommendations.push('👁️ High visibility but low clicks - review search intent alignment');
  }
  
  if (averagePosition <= 10 && averageCTR < 0.03) {
    recommendations.push('🎯 Top 10 position with low CTR - A/B test title tags for better click-through');
  }
  
  const topPerformers = keywords.filter(k => k.position <= 5).length;
  const lowPerformers = keywords.filter(k => k.position > 20).length;
  
  if (lowPerformers > keywords.length * 0.5) {
    recommendations.push('🔍 Over 50% of keywords rank poorly - consider content consolidation or expansion');
  }
  
  if (topPerformers > 0 && topPerformers < keywords.length * 0.3) {
    recommendations.push('⭐ Build on your top performers - create supporting content to strengthen this cluster');
  }
  
  if (recommendations.length === 0) {
    recommendations.push('✅ Cluster performing well - maintain content quality and monitor position changes');
  }
  
  // Determine difficulty (based on average position - how hard to improve)
  let difficulty: 'easy' | 'medium' | 'hard';
  if (averagePosition <= 10) difficulty = 'easy'; // Already ranking well, easier to optimize
  else if (averagePosition <= 20) difficulty = 'medium';
  else difficulty = 'hard'; // Far from page 1, harder to improve
  
  // Determine opportunity (based on potential clicks)
  let opportunity: 'high' | 'medium' | 'low';
  const potentialClicks = Math.round(totalImpressions * 0.1); // 10% CTR is achievable with optimization
  const clickGap = potentialClicks - totalClicks;
  
  if (clickGap > 100 && totalImpressions > 500) opportunity = 'high';
  else if (clickGap > 20 && totalImpressions > 100) opportunity = 'medium';
  else opportunity = 'low';
  
  // Determine priority (combination of opportunity and difficulty)
  let priority: 'high' | 'medium' | 'low';
  if (opportunity === 'high' && difficulty === 'easy') priority = 'high';
  else if (opportunity === 'high' && difficulty === 'medium') priority = 'high';
  else if (opportunity === 'medium' && difficulty === 'easy') priority = 'high';
  else if (opportunity === 'medium' && difficulty === 'medium') priority = 'medium';
  else if (opportunity === 'high' && difficulty === 'hard') priority = 'medium';
  else priority = 'low';

  // Calculate trend indicator based on current performance
  let trendIndicator: 'improving' | 'stable' | 'declining';
  if (healthScore >= 70 && averagePosition <= 10) {
    trendIndicator = 'improving';
  } else if (healthScore >= 50 && averageCTR >= expectedCTR * 0.8) {
    trendIndicator = 'stable';
  } else if (healthScore < 50 || averagePosition > 20) {
    trendIndicator = 'declining';
  } else {
    trendIndicator = 'stable';
  }

  // Generate forecast message
  let forecastMessage = '';
  if (trendIndicator === 'improving') {
    const clickIncrease = Math.round(potentialClicks - totalClicks);
    forecastMessage = `Strong performance trend. With optimization, expect +${clickIncrease} clicks/month (+${Math.round((clickIncrease / Math.max(totalClicks, 1)) * 100)}%)`;
  } else if (trendIndicator === 'stable') {
    const clickIncrease = Math.round((potentialClicks - totalClicks) * 0.5);
    forecastMessage = `Stable performance. Moderate optimization could yield +${clickIncrease} clicks/month`;
  } else {
    const atRiskClicks = Math.round(totalClicks * 0.3);
    forecastMessage = `Declining trend. Risk losing ${atRiskClicks} clicks/month without immediate action`;
  }

  return {
    totalClicks,
    totalImpressions,
    averagePosition,
    averageCTR,
    topPages,
    healthScore,
    healthGrade,
    recommendations,
    priority,
    difficulty,
    opportunity,
    trendIndicator,
    forecastMessage,
    potentialClicks
  };
};

// Word-based clustering: Group keywords with common root words (single assignment per keyword)
export const generateWordBasedClusters = (data: EnhancedSearchConsoleData[], minClusterSize = 10): TopicClustersResult => {
  const clusters: TopicCluster[] = [];
  
  // Common stop words to ignore
  const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'can', 'shall']);
  
  // Extract significant words from queries (2+ chars, not stop words)
  const getSignificantWords = (query: string): string[] => {
    if (!query || typeof query !== 'string') return [];
    return query.toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length >= 2 && !stopWords.has(word))
      .slice(0, 5); // Limit to first 5 significant words
  };

  // Step 1: Identify all potential cluster patterns
  const potentialClusters = new Map<string, EnhancedSearchConsoleData[]>();
  
  data.forEach(keyword => {
    const significantWords = getSignificantWords(keyword.query);
    
    // Generate word combinations (1-3 words)
    for (let i = 0; i < significantWords.length; i++) {
      for (let j = i + 1; j <= Math.min(i + 3, significantWords.length); j++) {
        const wordCombo = significantWords.slice(i, j).sort().join(' ');
        
        if (wordCombo.length >= 3) { // Minimum meaningful word combo
          if (!potentialClusters.has(wordCombo)) {
            potentialClusters.set(wordCombo, []);
          }
          potentialClusters.get(wordCombo)!.push(keyword);
        }
      }
    }
  });

  // Step 2: Filter potential clusters by minimum size
  const validClusters = new Map<string, EnhancedSearchConsoleData[]>();
  potentialClusters.forEach((keywords, wordCombo) => {
    if (keywords.length >= minClusterSize) {
      validClusters.set(wordCombo, keywords);
    }
  });

  // Step 3: Assign each keyword to exactly one cluster using scoring
  const keywordAssignments = new Map<string, string>(); // keyword query -> cluster key
  const finalClusters = new Map<string, EnhancedSearchConsoleData[]>();

  // Initialize final clusters
  validClusters.forEach((_, clusterKey) => {
    finalClusters.set(clusterKey, []);
  });

  // Score each keyword for each valid cluster and assign to best one
  data.forEach(keyword => {
    const significantWords = getSignificantWords(keyword.query);
    let bestCluster: string | null = null;
    let bestScore = 0;

    validClusters.forEach((clusterKeywords, clusterKey) => {
      // Check if this keyword could belong to this cluster
      const clusterWords = clusterKey.split(' ');
      const matchingWords = clusterWords.filter(word => significantWords.includes(word)).length;
      
      if (matchingWords > 0) {
        // Score = (matching words / cluster words) * (keyword importance)
        const wordMatchRatio = matchingWords / clusterWords.length;
        const keywordImportance = keyword.clicks + (keyword.impressions * 0.01); // Weight clicks more heavily
        const score = wordMatchRatio * keywordImportance;
        
        if (score > bestScore) {
          bestScore = score;
          bestCluster = clusterKey;
        }
      }
    });

    // Assign keyword to best cluster (if any)
    if (bestCluster) {
      keywordAssignments.set(keyword.query, bestCluster);
      finalClusters.get(bestCluster)!.push(keyword);
    }
  });

  // Step 4: Create final clusters, filtering again by minimum size after assignment
  let clusterId = 1;
  finalClusters.forEach((keywords, wordCombo) => {
    if (keywords.length >= minClusterSize) {
      const metrics = calculateClusterMetrics(keywords);
      
      // Create a meaningful cluster name
      const clusterName = wordCombo.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ') + ' Keywords';
      
      clusters.push({
        id: `word-cluster-${clusterId++}`,
        name: clusterName,
        keywords,
        ...metrics
      });
    }
  });

  // Sort clusters by total clicks (descending)
  clusters.sort((a, b) => b.totalClicks - a.totalClicks);

  return {
    clusters,
    method: 'word-based',
    totalClusters: clusters.length,
    totalKeywordsInClusters: clusters.reduce((sum, cluster) => sum + cluster.keywords.length, 0)
  };
};

// Semantic similarity clustering: Group conceptually related keywords
export const generateSemanticClusters = (data: EnhancedSearchConsoleData[], minClusterSize = 10): TopicClustersResult => {
  const clusters: TopicCluster[] = [];
  
  // Define semantic groups based on common SEO/business themes
  const semanticGroups: { [key: string]: string[] } = {
    'Web Design & Development': [
      'web design', 'website design', 'web development', 'website development', 'web developer', 
      'website builder', 'responsive design', 'ui design', 'ux design', 'front end', 'backend',
      'html', 'css', 'javascript', 'react', 'angular', 'vue', 'nodejs'
    ],
    'SEO & Marketing': [
      'seo', 'search engine optimization', 'digital marketing', 'content marketing', 'social media',
      'google ads', 'ppc', 'sem', 'analytics', 'keyword research', 'link building', 'serp'
    ],
    'E-commerce & Sales': [
      'ecommerce', 'online store', 'shopping cart', 'payment', 'checkout', 'product page',
      'sales', 'conversion', 'revenue', 'shopify', 'woocommerce', 'magento'
    ],
    'Content & Blogging': [
      'blog', 'content', 'article', 'post', 'writing', 'copywriting', 'content strategy',
      'editorial', 'publishing', 'cms', 'wordpress'
    ],
    'Local Business & Services': [
      'local', 'near me', 'city', 'location', 'address', 'phone', 'contact', 'service area',
      'business hours', 'appointment', 'consultation', 'quote'
    ],
    'Technology & Software': [
      'software', 'app', 'application', 'tool', 'platform', 'system', 'integration',
      'api', 'database', 'server', 'cloud', 'hosting'
    ]
  };

  // Function to check if a query matches semantic themes
  const getSemanticMatch = (query: string): string | null => {
    if (!query || typeof query !== 'string') return null;
    const lowerQuery = query.toLowerCase();
    
    for (const [theme, keywords] of Object.entries(semanticGroups)) {
      for (const keyword of keywords) {
        if (lowerQuery.includes(keyword.toLowerCase())) {
          return theme;
        }
      }
    }
    return null;
  };

  // Group keywords by semantic themes
  const themeGroups = new Map<string, EnhancedSearchConsoleData[]>();
  
  data.forEach(keyword => {
    const theme = getSemanticMatch(keyword.query);
    if (theme) {
      if (!themeGroups.has(theme)) {
        themeGroups.set(theme, []);
      }
      themeGroups.get(theme)!.push(keyword);
    }
  });

  // Convert theme groups to clusters
  let clusterId = 1;
  themeGroups.forEach((keywords, theme) => {
    if (keywords.length >= minClusterSize) {
      const metrics = calculateClusterMetrics(keywords);
      
      clusters.push({
        id: `semantic-cluster-${clusterId++}`,
        name: theme,
        keywords,
        ...metrics
      });
    }
  });

  // Sort clusters by total clicks (descending)
  clusters.sort((a, b) => b.totalClicks - a.totalClicks);

  return {
    clusters,
    method: 'semantic-similarity',
    totalClusters: clusters.length,
    totalKeywordsInClusters: clusters.reduce((sum, cluster) => sum + cluster.keywords.length, 0)
  };
};

// Page-based clustering: Group keywords by the pages they rank for
export const generatePageBasedClusters = (data: EnhancedSearchConsoleData[], minClusterSize = 10): TopicClustersResult => {
  const clusters: TopicCluster[] = [];
  
  // Group keywords by page URL
  const pageGroups = new Map<string, EnhancedSearchConsoleData[]>();
  
  data.forEach(keyword => {
    if (keyword.page) {
      if (!pageGroups.has(keyword.page)) {
        pageGroups.set(keyword.page, []);
      }
      pageGroups.get(keyword.page)!.push(keyword);
    }
  });

  // Convert page groups to clusters
  let clusterId = 1;
  pageGroups.forEach((keywords, page) => {
    if (keywords.length >= minClusterSize) {
      const metrics = calculateClusterMetrics(keywords);
      
      // Create a readable cluster name from the page URL
      let clusterName = page;
      
      // Clean up the URL for display
      clusterName = clusterName.replace(/^https?:\/\/[^/]+/, ''); // Remove domain
      clusterName = clusterName.replace(/\/$/, '') || '/'; // Remove trailing slash
      
      // If it's a long path, show the last meaningful part
      if (clusterName.includes('/')) {
        const pathParts = clusterName.split('/').filter(Boolean);
        if (pathParts.length > 2) {
          clusterName = '.../' + pathParts.slice(-2).join('/');
        }
      }
      
      // Limit length and add "Page" suffix
      if (clusterName.length > 40) {
        clusterName = clusterName.substring(0, 37) + '...';
      }
      clusterName += ' Page';

      clusters.push({
        id: `page-cluster-${clusterId++}`,
        name: clusterName,
        keywords,
        ...metrics
      });
    }
  });

  // Sort clusters by total clicks (descending)
  clusters.sort((a, b) => b.totalClicks - a.totalClicks);

  return {
    clusters,
    method: 'page-based',
    totalClusters: clusters.length,
    totalKeywordsInClusters: clusters.reduce((sum, cluster) => sum + cluster.keywords.length, 0)
  };
};

// Main function to generate clusters based on selected method
export const generateTopicClusters = (
  data: EnhancedSearchConsoleData[], 
  method: 'word-based' | 'semantic-similarity' | 'page-based',
  minClusterSize = 10
): TopicClustersResult => {
  switch (method) {
    case 'word-based':
      return generateWordBasedClusters(data, minClusterSize);
    case 'semantic-similarity':
      return generateSemanticClusters(data, minClusterSize);
    case 'page-based':
      return generatePageBasedClusters(data, minClusterSize);
    default:
      return {
        clusters: [],
        method,
        totalClusters: 0,
        totalKeywordsInClusters: 0
      };
  }
};